import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, adminApi, formatIst, useAuth } from '@wa/shared';
import { Cell, DataTable, Empty, Page } from '@/components/Primitives';

/**
 * PSM's own accounts.
 *
 * Two refusals live on the server and are surfaced here rather than hidden: nobody
 * may deactivate or demote themselves, and the last active superuser may not be
 * demoted by anyone. A platform nobody can sign into is recoverable only by editing
 * the database, which is the thing this screen exists to stop.
 */
export function StaffPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const staff = useQuery({ queryKey: ['admin', 'platform-users'], queryFn: adminApi.platformUsers });

  const done = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'platform-users'] });
  };

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const create = useMutation({
    mutationFn: adminApi.createPlatformUser,
    onSuccess: () => { done(); setAdding(false); },
    onError: (e) => fail(e, 'Could not create that account.'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { isActive?: boolean; isSuperuser?: boolean } }) =>
      adminApi.updatePlatformUser(id, patch),
    onSuccess: done,
    onError: (e) => fail(e, 'Could not change that account.'),
  });

  const rows = staff.data ?? [];
  const superusers = rows.filter((r) => r.isSuperuser && r.isActive).length;

  return (
    <Page title="Staff" subtitle="Who at PSM can sign in to the platform.">
      {error && (
        <p role="alert" className="mb-3 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      <div className="mb-4">
        <button
          type="button"
          onClick={() => setAdding(!adding)}
          className="rounded bg-(--color-brand) px-3 py-1.5 text-body font-medium text-white"
        >
          {adding ? 'Cancel' : 'Add somebody'}
        </button>
      </div>

      {adding && (
        <form
          className="mb-6 grid gap-3 rounded border border-(--color-line) bg-(--color-surface) p-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            create.mutate({
              email: String(form.get('email') ?? '').trim(),
              displayName: String(form.get('displayName') ?? '').trim(),
              password: String(form.get('password') ?? ''),
              isSuperuser: form.get('isSuperuser') === 'on',
            });
          }}
        >
          <label className="text-small">
            <span className="mb-1 block text-(--color-muted)">Name</span>
            <input name="displayName" required
                   className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
          </label>
          <label className="text-small">
            <span className="mb-1 block text-(--color-muted)">Email</span>
            <input name="email" type="email" required
                   className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
          </label>
          <label className="text-small">
            <span className="mb-1 block text-(--color-muted)">Password (at least 12 characters)</span>
            <input name="password" type="password" minLength={12} required
                   className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
          </label>
          <label className="flex items-end gap-2 text-small">
            <input name="isSuperuser" type="checkbox" className="mb-2" />
            <span className="mb-2">Superuser — can manage staff</span>
          </label>
          <p className="text-small text-(--color-muted) sm:col-span-2">
            Pass the password on yourself, or tell them to use <b>Forgot password</b> on the
            sign-in screen.
          </p>
          <div className="sm:col-span-2">
            <button type="submit" disabled={create.isPending}
                    className="rounded bg-(--color-brand) px-3 py-1.5 text-body font-medium text-white disabled:opacity-50">
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <DataTable head={['Name', 'Email', 'Role', 'Last signed in', '']}>
        {rows.map((row) => {
          const isSelf = user?.email === row.email;
          const isLastSuperuser = row.isSuperuser && row.isActive && superusers === 1;

          return (
            <tr key={row.id} className={row.isActive ? '' : 'opacity-60'}>
              <Cell>{row.displayName}{isSelf && <span className="ml-2 text-micro text-(--color-muted)">you</span>}</Cell>
              <Cell mono>{row.email}</Cell>
              <Cell nowrap>
                {row.isSuperuser ? 'Superuser' : 'Staff'}
                {!row.isActive && <span className="ml-2 text-(--color-warn)">deactivated</span>}
              </Cell>
              <Cell nowrap>{formatIst(row.lastLoginAt, 'never')}</Cell>
              <Cell nowrap>
                {/* A control the API will refuse is worse than an absent one. */}
                {!isSelf && !isLastSuperuser && (
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: row.id, patch: { isActive: !row.isActive } })}
                    className="rounded border border-(--color-line) px-2 py-1 text-small hover:text-(--color-ink)"
                  >
                    {row.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                )}
                {isLastSuperuser && !isSelf && (
                  <span className="text-micro text-(--color-muted)">last superuser</span>
                )}
              </Cell>
            </tr>
          );
        })}
      </DataTable>

      {rows.length === 0 && !staff.isLoading && <Empty>No staff accounts.</Empty>}
    </Page>
  );
}
